import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import {
  Combobox as HeadlessCombobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react"
import { cn } from "@/lib/utils"

export interface ComboboxOption {
  value: string
  label: string
}

interface ComboboxProps {
  options: ComboboxOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = "Seleccionar...",
  className,
}: ComboboxProps) {
  const [query, setQuery] = React.useState("")

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase()),
        )

  const selectedOption = options.find((option) => option.value === value)

  return (
    <HeadlessCombobox value={value} onChange={(val) => onChange(val || "")}>
      <div className={cn("relative", className)}>
        <div className="relative w-full">
          <ComboboxInput
            className="border-input file:text-foreground placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border bg-transparent px-3 py-1 pr-10 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
            displayValue={() => selectedOption?.label || ""}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={placeholder}
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronsUpDown className="text-muted-foreground h-4 w-4" />
          </ComboboxButton>
        </div>
        <ComboboxOptions className="bg-popover ring-opacity-5 absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border py-1 text-base shadow-lg ring-1 ring-black focus:outline-none sm:text-sm">
          {filteredOptions.length === 0 && query !== "" ? (
            <div className="text-muted-foreground relative cursor-default px-4 py-2 select-none">
              No se encontraron resultados.
            </div>
          ) : (
            filteredOptions.map((option) => (
              <ComboboxOption
                key={option.value}
                value={option.value}
                className={({ focus, selected }) =>
                  cn(
                    "relative cursor-default py-2 pr-4 pl-10 select-none",
                    focus && "bg-accent text-accent-foreground",
                    selected && "font-medium",
                  )
                }
              >
                {({ selected }) => (
                  <>
                    <span
                      className={cn(
                        "block truncate",
                        selected && "font-medium",
                      )}
                    >
                      {option.label}
                    </span>
                    {selected && (
                      <span className="text-primary absolute inset-y-0 left-0 flex items-center pl-3">
                        <Check className="h-4 w-4" />
                      </span>
                    )}
                  </>
                )}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </div>
    </HeadlessCombobox>
  )
}
